<script setup lang="ts">
import { ref } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import { serviceCompaniesApi, type ServiceCompany } from '@/api/service-companies';

interface Props {
  serviceCompany: ServiceCompany;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const submitButtonDisabled = ref(false);

const discardMutation = useMutation({
  mutationFn: () => serviceCompaniesApi.destroy(props.serviceCompany.id),
  onSuccess: () => {
    emit('success');
    emit('close');
  },
  onError: () => {
    submitButtonDisabled.value = false;
  },
});

function handleSubmit() {
  submitButtonDisabled.value = true;
  discardMutation.mutate();
}
</script>
<template>
  <v-card class="pa-4">
    <v-card-title>
      Archivar Empresa de Servicios "{{ serviceCompany.taxName }}"
    </v-card-title>
    <v-card-text>
      <p class="mb-4">
        ¿Está seguro que desea archivar la empresa de servicios "{{ serviceCompany.taxName }}"?
      </p>
      <p class="mb-4 text-body-2 text-medium-emphasis">
        La empresa será archivada y ya no estará disponible para asignar a nuevos profesionales.
        Esta acción se puede revertir más tarde si es necesario.
      </p>
      <v-alert
        type="warning"
        variant="tonal"
        class="mb-4"
      >
        Los profesionales actualmente asignados a esta empresa mantendrán su asignación.
      </v-alert>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="discardMutation.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="warning"
        :loading="discardMutation.isPending.value"
        :disabled="submitButtonDisabled"
        @click="handleSubmit"
      >
        Archivar
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="discardMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Ha ocurrido un error. Intenta nuevamente.
    </v-alert>
  </v-card>
</template>
